import React, { useMemo } from "react";
import { Box, CircularProgress, Paper, Typography } from "@mui/material";
import { SxProps, Theme } from "@mui/material/styles";

type Props = {
  title?: string;
  content?: string | null;
  loading?: boolean;
  emptyMessage?: string;
  minHeight?: number;
  paperSx?: SxProps<Theme>;
};

const JsonTemplatePreview: React.FC<Props> = ({
  title = "Template Preview",
  content,
  loading = false,
  emptyMessage = "Select an uploaded template or choose a JSON file to preview it here.",
  minHeight = 420,
  paperSx,
}) => {
  const previewNode = useMemo(() => {
    if (!content) {
      return (
        <Typography sx={{ color: "rgba(255,255,255,0.6)" }}>
          {emptyMessage}
        </Typography>
      );
    }

    try {
      return (
        <Box
          component="pre"
          sx={{
            m: 0,
            color: "#fff",
            fontSize: { xs: 12, sm: 13 },
            whiteSpace: "pre-wrap",
            wordBreak: "break-word",
            fontFamily: "Consolas, Monaco, 'Courier New', monospace",
          }}
        >
          {JSON.stringify(JSON.parse(content), null, 2)}
        </Box>
      );
    } catch {
      return <Typography color="error">Invalid JSON content.</Typography>;
    }
  }, [content, emptyMessage]);

  return (
    <Box sx={{ height: "100%", display: "flex", flexDirection: "column", minHeight: 0 }}>
      <Typography
        sx={{
          mb: 1,
          fontWeight: 600,
          color: "hsl(19 101% 51% / 1)",
          fontSize: { xs: "0.95rem", sm: "1rem" },
          wordBreak: "break-word",
        }}
      >
        {title}
      </Typography>

      <Paper
        sx={{
          background: "#111",
          p: { xs: 1.25, sm: 2 },
          flex: 1,
          minHeight: { xs: Math.min(minHeight, 280), sm: minHeight },
          maxHeight: { xs: 360, md: "none" },
          overflow: "auto",
          border: "1px solid #333",
          display: "flex",
          alignItems: loading ? "center" : "flex-start",
          justifyContent: loading ? "center" : "flex-start",
          WebkitOverflowScrolling: "touch",
          ...paperSx,
        }}
      >
        {loading ? (
          <CircularProgress size={28} sx={{ color: "hsl(19 101% 51% / 1)" }} />
        ) : (
          previewNode
        )}
      </Paper>
    </Box>
  );
};

export default JsonTemplatePreview;
